import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { loginInput } from '@brechoflow/contracts';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { contractResolver } from '@/lib/forms';
import { errorMessage } from '@/lib/api';
import { authApi } from '../auth-api';
import { landingFor } from '../guards';
import { useSession } from '../session';

type Values = z.input<typeof loginInput>;

export function LoginPage() {
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values, unknown, z.output<typeof loginInput>>({ resolver: contractResolver(loginInput), defaultValues: { email: '', password: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.login(values);
      const me = await authApi.me();
      setMe(me);
      navigate(landingFor(me, params.get('next')), { replace: true });
    } catch (e) {
      setFormError(errorMessage(e));
    }
  });

  return (
    <>
      <h1 className="font-display text-3xl font-semibold">Entrar</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Acesse a sua loja.</p>
      <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {formError}
          </p>
        )}
        <FormField label="E-mail" error={errors.email?.message}>
          {(a) => <Input type="email" autoComplete="username" autoFocus {...a} {...register('email')} />}
        </FormField>
        <FormField label="Senha" error={errors.password?.message}>
          {(a) => <PasswordInput autoComplete="current-password" {...a} {...register('password')} />}
        </FormField>
        <div className="-mt-1 text-right text-sm">
          <Link to="/esqueci-senha" className="text-primary underline-offset-4 hover:underline">
            Esqueci minha senha
          </Link>
        </div>
        <Button type="submit" size="lg" loading={isSubmitting}>
          Entrar
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground">
        Ainda não tem conta?{' '}
        <Link to="/cadastro" className="font-medium text-primary underline-offset-4 hover:underline">
          Cadastrar minha loja
        </Link>
      </p>
    </>
  );
}
